import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";
import { Psbt } from "bitcoinjs-lib";
import { Address, Msg, Network, Signer, Simulation, TxResult } from "rujira.js";
import { CosmosAdapter } from "./cosmos";
import { Eip6963Adapter } from "./eip6963";
import { walletType } from "./utils";
import { UtxoContext } from "./utxo";

declare global {
  interface Window extends KeplrWindow {}
}

export class KeplrAdapter implements Signer {
  private c: CosmosAdapter;
  constructor(
    private e: Eip6963Adapter,
    k: () => Keplr | undefined
  ) {
    this.c = new CosmosAdapter(k);
  }
  onChange = (cb: () => void) => {
    window.addEventListener("keplr_keystorechange", cb);
    this.e.onChange(cb);
  };

  isAvailable(): boolean {
    return this.c.isAvailable();
  }

  public async connect(): Promise<Address[]> {
    return Promise.allSettled([
      this.c.connect(),
      this.e.connect(),
      this.getBitcoinAccounts(),
    ]).then((x) =>
      x.reduce(
        (a: Address[], v) =>
          v.status === "fulfilled" ? [...v.value, ...a] : a,
        []
      )
    );
  }

  async getBitcoinAccounts(): Promise<Address[]> {
    const k = window.keplr?.bitcoin;
    if (!k) throw new Error("No Keplr Bitcoin provider");
    return k.connectWallet();
  }

  public async simulate(msg: Msg): Promise<Simulation> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.simulate(msg);
      case "evm":
        return this.e.simulate(msg);
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by Keplr Wallet`);

      default:
        const context = new UtxoContext(msg.account.address);
        const utxos = await context.fetchUtxos();
        const { fee } = await msg.toPsbt(utxos);

        return {
          symbol: "BTC",
          decimals: 8,
          amount: fee,
          gas: 0n,
        };
    }
  }

  public async signAndBroadcast(
    simulation: Simulation,
    msg: Msg
  ): Promise<TxResult> {
    const type = walletType(msg.account.address);
    switch (type) {
      case "cosmos":
        return this.c.signAndBroadcast(simulation, msg);
      case "evm":
        return this.e.signAndBroadcast(simulation, msg);
      default:
        const context = new UtxoContext(msg.account.address);
        const utxos = await context.fetchUtxos();
        const { psbt } = await msg.toPsbt(utxos);

        const res = await window.keplr.bitcoin.signPsbt(psbt.toHex(), {
          autoFinalized: true,
        });
        const signed = Psbt.fromHex(res);
        return context.broadcast(
          msg.account.address,
          signed.extractTransaction(),
          msg.toDeposit?.()
        );
    }
  }
  public networks(): Network[] {
    return ["BTC", ...this.c.networks(), ...this.e.networks()];
  }
}

const provider = new KeplrAdapter(
  new Eip6963Adapter("app.keplr"),
  () => window.keplr
);

export default provider;
